import { useIntl } from 'react-intl';
import { EmailTemplate } from '@rossum/api-client/emailTemplates';
import { Box, Typography } from '@rossum/ui/material';
import HTMLMessage from '../../../components/DatapointMessage/HTMLMessage';
import { ValidationErrorsPerQueue } from '../hooks/useValidateRuleAgainstQueues';
import { categorizeValidationMessages } from '../utils/categorizeValidationMessages';

type MoveToAnotherQueueValidationResultProps = {
  validationErrors: ValidationErrorsPerQueue['errors'];
  targetQueueName: string;
  unavailableEmailTemplates: Pick<EmailTemplate, 'name'>[];
};

export const MoveToAnotherQueueValidationResult = ({
  validationErrors,
  targetQueueName,
  unavailableEmailTemplates,
}: MoveToAnotherQueueValidationResultProps) => {
  const intl = useIntl();
  const hasEmailTemplate = unavailableEmailTemplates.length > 0;

  const { conditionErrors, actionErrors, otherErrors } =
    categorizeValidationMessages(validationErrors);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography>
        {intl.formatMessage(
          {
            id: 'features.queueSettings.rules.list.moveToAnotherQueueDialog.validationResult.description',
          },
          { queueName: <strong>{targetQueueName}</strong> }
        )}
      </Typography>

      {conditionErrors.length > 0 && (
        <Box>
          <Typography variant="body2" fontWeight="bold" sx={{ mb: 0.5 }}>
            {intl.formatMessage({
              id: 'features.queueSettings.rules.list.moveToAnotherQueueDialog.validationResult.conditionNotValid',
            })}
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {conditionErrors.map(msg => (
              <Typography component="li" variant="body2" key={msg.content}>
                <Box component="span" sx={{ color: 'text.secondary' }}>
                  {msg.content}
                </Box>
              </Typography>
            ))}
          </Box>
        </Box>
      )}

      {actionErrors.length > 0 && (
        <Box>
          <Typography variant="body2" fontWeight="bold" sx={{ mb: 0.5 }}>
            {intl.formatMessage({
              id: 'features.queueSettings.rules.list.moveToAnotherQueueDialog.validationResult.actionNotValid',
            })}
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {actionErrors.map(msg => (
              <Typography component="li" variant="body2" key={msg.content}>
                <Box component="span" sx={{ color: 'text.secondary' }}>
                  {msg.content}
                </Box>
              </Typography>
            ))}
          </Box>
        </Box>
      )}

      {otherErrors.length > 0 && (
        <Box>
          <Typography variant="body2" fontWeight="bold" sx={{ mb: 0.5 }}>
            {intl.formatMessage({
              id: 'features.queueSettings.rules.list.moveToAnotherQueueDialog.validationResult.otherErrors',
            })}
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {otherErrors.map(msg => (
              <Typography component="li" variant="body2" key={msg.content}>
                <HTMLMessage content={msg.content} />
              </Typography>
            ))}
          </Box>
        </Box>
      )}

      {hasEmailTemplate && (
        <Box>
          <Typography variant="body2" fontWeight="bold" sx={{ mb: 0.5 }}>
            {intl.formatMessage(
              {
                id: 'features.queueSettings.rules.list.moveToAnotherQueueDialog.validationResult.emailTemplateWarning',
              },
              {
                count: unavailableEmailTemplates.length,
                templates: unavailableEmailTemplates
                  .map(t => `"${t.name}"`)
                  .join(', '),
              }
            )}
          </Typography>
        </Box>
      )}

      <Typography variant="body2" color="text.secondary">
        {intl.formatMessage({
          id: 'features.queueSettings.rules.list.moveToAnotherQueueDialog.helpText',
        })}
      </Typography>
    </Box>
  );
};
